import { CHAT_RANGE, chatRoom, slotOn } from "../hud/area-slots.ts";
import { HUD_SLOTS, type HudArea, type SlotRect } from "../hud/slots.ts";
import { readSetting, writeSetting, type SettingsStore } from "../settings.ts";

/**
 * The chat panel's size as plain data (P1.9-T42, ADR-061's amendment "the chat panel resizes"; the design's grip and
 * its `small`, `large` and `resizing` states, a visual reference): the viewer's wish, kept per browser, within the
 * range; what of it fits an area beside the HUD's fixed parts; the rectangle, anchored at the slot's bottom-left
 * corner so a larger panel grows up and right; Settings' three choices; and what a drag of the grip and its keys
 * do. Every size is whole logical pixels. Pure: the page holds the size and hands it in.
 */

/**
 * A size in logical pixels.
 */
export interface ChatSize {
  readonly width: number;
  readonly height: number;
}

/**
 * The range a size is kept in.
 */
export const CHAT_SIZE_RANGE = CHAT_RANGE;

/**
 * Settings' Chat size choices: the range's least, the slot's own size, and the design's large.
 */
export const CHAT_SIZES = { small: CHAT_RANGE.min, medium: { width: HUD_SLOTS.chat.width, height: HUD_SLOTS.chat.height }, large: { width: 300, height: 180 } } as const satisfies Record<string, ChatSize>;

/**
 * One of Settings' Chat size choices.
 */
export type ChatSizeChoice = keyof typeof CHAT_SIZES;

/**
 * The storage key the size is kept under.
 */
export const CHAT_SIZE_KEY = "odyssey.chat.size";

/**
 * How far a key of the grip moves the size, and how far with Shift held, in logical pixels.
 */
export const CHAT_KEY_STEP = { plain: 1, shift: 8 } as const;

/**
 * A kept size's form: two runs of digits around an x.
 */
const KEPT = /^(\d{1,4})x(\d{1,4})$/;

/**
 * Puts a size in the range, on whole logical pixels.
 *
 * @param size The size.
 * @returns The size rounded and clamped on each axis, or the default for a size that is no finite number.
 * @example
 * clampChatSize({ width: 500, height: 50 });
 * // { width: 320, height: 90 }
 */
export function clampChatSize(size: ChatSize): ChatSize {
  if (!Number.isFinite(size.width) || !Number.isFinite(size.height)) {
    return CHAT_SIZES.medium;
  }
  const { min, max } = CHAT_SIZE_RANGE;
  return { width: Math.min(max.width, Math.max(min.width, Math.round(size.width))), height: Math.min(max.height, Math.max(min.height, Math.round(size.height))) };
}

/**
 * What of a size an area shows: the size, cut to the room the panel may take there (`chatRoom`), so the panel never
 * covers the status row, never leaves the screen and never covers the ability bar. Under 632 wide, where the bar pages,
 * the room runs to a gutter left of the paged bar, never under the slot's own 198. The kept size is not changed, so a
 * window made larger again shows it whole.
 *
 * @param size The size wished for, within the range.
 * @param area The area, whole logical pixels.
 * @returns The size to draw.
 * @example
 * fitChatSize({ width: 300, height: 180 }, { width: 640, height: 360 });
 * // { width: 200, height: 180 }
 */
export function fitChatSize(size: ChatSize, area: HudArea): ChatSize {
  const room = chatRoom(area);
  return { width: Math.min(size.width, room.width), height: Math.min(size.height, room.height) };
}

/**
 * The panel's rectangle on an area: the size at the bottom-left corner of the chat slot as it stands there, which is
 * above the XP bar where the ability bar reaches the slot (`slotOn`).
 *
 * @param size The size to draw.
 * @param area The area, whole logical pixels.
 * @returns Its rectangle from the area's top left.
 * @example
 * chatRect({ width: 160, height: 90 }, { width: 640, height: 360 });
 * // { left: 4, top: 266, width: 160, height: 90 }
 */
export function chatRect(size: ChatSize, area: HudArea): SlotRect {
  const slot = slotOn("chat", area);
  return { left: slot.x, top: Math.floor(area.height) - slot.y - size.height, width: size.width, height: size.height };
}

/**
 * The choice a size is, if it is exactly one of Settings' three.
 *
 * @param size The size.
 * @returns The choice, or null for a size the grip left between them.
 */
export function chatSizeChoice(size: ChatSize): ChatSizeChoice | null {
  return (Object.keys(CHAT_SIZES) as ChatSizeChoice[]).find((choice) => CHAT_SIZES[choice].width === size.width && CHAT_SIZES[choice].height === size.height) ?? null;
}

/**
 * A size as it is kept.
 *
 * @param size The size.
 * @returns "WIDTHxHEIGHT".
 */
export function formatChatSize(size: ChatSize): string {
  return `${size.width}x${size.height}`;
}

/**
 * Reads a kept value as a size.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns The size it names, clamped to the range, or the default for anything that is not two whole numbers
 *   around an x.
 */
export function parseChatSize(raw: string | null): ChatSize {
  const match = raw === null ? null : KEPT.exec(raw);
  return match === null ? CHAT_SIZES.medium : clampChatSize({ width: Number(match[1]), height: Number(match[2]) });
}

/**
 * Reads the kept size.
 *
 * @param store Where it is kept, or null when there is none.
 * @returns The size, the default when nothing usable is kept or the store cannot be read.
 */
export function readChatSize(store: SettingsStore | null): ChatSize {
  return readSetting(CHAT_SIZE_KEY, parseChatSize, store);
}

/**
 * Keeps a size.
 *
 * @param size The size.
 * @param store Where to keep it, or null when there is none.
 * @returns True when the store took it.
 */
export function writeChatSize(size: ChatSize, store: SettingsStore | null): boolean {
  return writeSetting(CHAT_SIZE_KEY, formatChatSize(size), store);
}

/**
 * The wish after the grip moved the size drawn by a step on each axis. An axis the grip did not move keeps its wish,
 * so a press with no travel changes nothing. An axis it moved takes the size drawn plus the step, within the range
 * and the room the area has; where that reaches the room's limit while the wish is larger than the limit, the wish
 * stays, so pushing against a small window never shrinks what a larger window will show.
 *
 * @param wish The size kept.
 * @param step How far the grip moved the size on each axis, in whole logical pixels.
 * @param area The area, whole logical pixels.
 * @returns The size to keep.
 */
function moveChatSize(wish: ChatSize, step: ChatSize, area: HudArea): ChatSize {
  const room = chatRoom(area);
  const drawn = fitChatSize(wish, area);
  const moved = clampChatSize({ width: drawn.width + step.width, height: drawn.height + step.height });
  /**
   * One axis of the wish.
   *
   * @param kept The wish on the axis.
   * @param by The step on the axis.
   * @param next The moved size on the axis, within the range.
   * @param limit The room on the axis.
   * @returns The wish to keep on the axis.
   */
  const axis = (kept: number, by: number, next: number, limit: number): number => {
    if (by === 0) {
      return kept;
    }
    return next >= limit && kept > limit ? kept : Math.min(next, limit);
  };
  return { width: axis(wish.width, step.width, moved.width, room.width), height: axis(wish.height, step.height, moved.height, room.height) };
}

/**
 * The wish a drag of the grip gives: the grip is the panel's top-right corner, so the pointer's travel right widens
 * the panel and its travel up heightens it, by the travel in logical pixels, rounded, from the size drawn as the
 * drag began, within the range and what fits the area. An axis with no travel, and one pushed to the area's limit
 * while the wish is larger, keep the wish (`moveChatSize`).
 *
 * @param wish The size kept as the drag began.
 * @param travel How far the pointer has moved since, in CSS pixels, right and down positive.
 * @param px CSS pixels to the logical pixel.
 * @param area The area, whole logical pixels.
 * @returns The size to keep, of which the panel draws what fits.
 * @example
 * dragChatSize({ width: 198, height: 122 }, { x: -20, y: 20 }, 2, { width: 640, height: 360 });
 * // { width: 188, height: 112 }
 */
export function dragChatSize(wish: ChatSize, travel: { readonly x: number; readonly y: number }, px: number, area: HudArea): ChatSize {
  return moveChatSize(wish, { width: Math.round(travel.x / px), height: Math.round(-travel.y / px) }, area);
}

/**
 * The wish a key of the focused grip gives: Right and Left a step wider and narrower, Up and Down a step taller and
 * shorter, a step 1 logical px and 8 with Shift held, from the size drawn, within the range and what fits the area;
 * a key at the area's limit keeps a larger wish (`moveChatSize`). Those four are the only keys the grip takes
 * (ADR-061's amendment of 2026-10-03).
 *
 * @param wish The size kept.
 * @param key The key, as `KeyboardEvent.key` names it.
 * @param shift Whether Shift is held.
 * @param area The area, whole logical pixels.
 * @returns The size to keep, or null for a key the grip does not take.
 */
export function keyChatSize(wish: ChatSize, key: string, shift: boolean, area: HudArea): ChatSize | null {
  const step = shift ? CHAT_KEY_STEP.shift : CHAT_KEY_STEP.plain;
  const steps: Readonly<Record<string, ChatSize>> = {
    ArrowRight: { width: step, height: 0 },
    ArrowLeft: { width: -step, height: 0 },
    ArrowUp: { width: 0, height: step },
    ArrowDown: { width: 0, height: -step },
  };
  const by = Object.hasOwn(steps, key) ? steps[key] : undefined;
  return by === undefined ? null : moveChatSize(wish, by, area);
}

/**
 * The size as it is shown while the panel is resized.
 *
 * @param size The size.
 * @returns "WIDTH x HEIGHT".
 */
export function sizeLabel(size: ChatSize): string {
  return `${size.width} x ${size.height}`;
}
